import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, CheckCheck, ExternalLink } from "lucide-react";
import api from "@/lib/api";

/**
 * NotificationBell — reusable header widget powering the FLOWTALIS
 * Notification Center. Polls the unread count every 30s, opens a dropdown
 * with the 20 most recent notifications, and supports mark-read / mark-all-read.
 *
 * Every future module (Production Tracker, Artwork Approval, Brand Vault,
 * Support Center, etc.) publishes into the same `/api/notifications` service —
 * this component surfaces them uniformly across admin + client portals.
 */
const NotificationBell = ({ theme = "light" }) => {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const loadCount = () => {
    api.get("/notifications/unread-count").then((r) => setCount(r.data.count || 0)).catch(() => {});
  };

  const loadList = () => {
    setLoading(true);
    api.get("/notifications?limit=20")
      .then((r) => setItems(r.data.notifications || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const markRead = async (id) => {
    try {
      await api.post("/notifications/mark-read", { ids: [id] });
      setItems((list) => list.map((x) => x.id === id ? { ...x, is_read: true } : x));
      loadCount();
    } catch { /* silent */ }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-read", {});
      setItems((list) => list.map((x) => ({ ...x, is_read: true })));
      setCount(0);
    } catch { /* silent */ }
  };

  const dark = theme === "dark";
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className={`relative h-10 w-10 rounded-full flex items-center justify-center transition-colors ${
          dark ? "text-white/70 hover:text-white hover:bg-white/10" : "text-[var(--ft-charcoal)] hover:bg-[var(--ft-light-gray)]"
        }`}
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell size={18} strokeWidth={1.75} />
        {count > 0 && (
          <span
            className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
            style={{ background: "var(--ft-gold)", color: "var(--ft-black)" }}
            data-testid="notification-count"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[380px] max-w-[92vw] rounded-xl shadow-2xl border border-[var(--ft-border)] bg-white z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="p-4 border-b border-[var(--ft-border)] flex items-center justify-between">
            <div>
              <div className="ft-eyebrow">Notifications</div>
              <div className="text-sm font-semibold mt-0.5">{count > 0 ? `${count} unread` : "You're all caught up"}</div>
            </div>
            {count > 0 && (
              <button onClick={markAll} className="text-xs font-semibold text-[var(--ft-gold)] hover:underline flex items-center gap-1" data-testid="notification-mark-all">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-sm text-[var(--ft-muted)]">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={28} className="mx-auto mb-3 text-[var(--ft-muted)]" />
                <p className="text-sm text-[var(--ft-muted)]">No notifications yet.</p>
              </div>
            ) : (
              <ul>
                {items.map((n) => (
                  <li key={n.id} className={`border-b border-[var(--ft-border)] last:border-0 ${!n.is_read ? "bg-[var(--ft-gold-soft)]" : ""}`} data-testid={`notification-${n.id}`}>
                    <div className="p-4 flex gap-3">
                      <div className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${!n.is_read ? "bg-[var(--ft-gold)]" : "bg-transparent border border-[var(--ft-border)]"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[var(--ft-black)] leading-snug">{n.title}</div>
                        {n.body && <div className="text-xs text-[var(--ft-muted)] mt-0.5 leading-relaxed">{n.body}</div>}
                        <div className="text-[0.65rem] text-[var(--ft-muted)] mt-1.5 flex items-center gap-2">
                          <span>{new Date(n.created_at).toLocaleString()}</span>
                          {n.actor && <><span>·</span><span>{n.actor}</span></>}
                        </div>
                        <div className="flex gap-3 mt-2">
                          {n.link && (
                            <Link to={n.link} onClick={() => { setOpen(false); markRead(n.id); }} className="text-[11px] font-semibold text-[var(--ft-gold)] hover:underline flex items-center gap-1">
                              Open <ExternalLink size={10} />
                            </Link>
                          )}
                          {!n.is_read && (
                            <button onClick={() => markRead(n.id)} className="text-[11px] text-[var(--ft-muted)] hover:text-[var(--ft-black)] flex items-center gap-1">
                              <Check size={10} /> Mark read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
